import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { QuoteService } from '../quote.service';

@Component({
  selector: 'app-quote-status-select',
  imports: [FormsModule, SelectModule, NgStyle, ProgressSpinnerModule],
  templateUrl: './quote-status-select.component.html',
  styleUrl: './quote-status-select.component.scss',
})
export class QuoteStatusSelectComponent implements OnInit, OnDestroy {
  private quoteService = inject(QuoteService);

  protected statusOptions = signal<{ label: string; value: string }[]>([]);
  protected isLoading = signal<boolean>(true);

  protected selectedStatus: string | null = null;

  isSaving = false;
  showCheckIcon = signal<boolean>(false);
  checkIconFading = signal<boolean>(false);
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  @Input() placeholder = 'Select a status';
  @Input() disabled = false;
  @Input()
  set value(status: string | null | undefined) {
    this.selectedStatus = status ?? null;
  }
  get value(): string | null {
    return this.selectedStatus;
  }

  async ngOnInit(): Promise<void> {
    await this.loadStatusOptions();
  }

  ngOnDestroy(): void {
    // Clean up timers to prevent memory leaks
    if (this.fadeTimer) {
      clearTimeout(this.fadeTimer);
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
  }

  protected async onStatusChange(status: string | null): Promise<void> {
    // Clear any existing timers to prevent glitches
    if (this.fadeTimer) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    this.isSaving = true;
    this.selectedStatus = status;
    this.showCheckIcon.set(false);
    this.checkIconFading.set(false);
    await this.quoteService.patchQuote({ status: status ?? '' });
    this.isSaving = false;
    this.showCheckIcon.set(true);

    // Start fade after 1 second
    this.fadeTimer = setTimeout(() => {
      this.checkIconFading.set(true);
      // Hide completely after fade animation completes
      this.hideTimer = setTimeout(() => {
        this.showCheckIcon.set(false);
        this.checkIconFading.set(false);
        this.fadeTimer = null;
        this.hideTimer = null;
      }, 500); // Match animation duration
    }, 1000);
  }

  private async loadStatusOptions(): Promise<void> {
    this.isLoading.set(true);
    try {
      const labels = await this.quoteService.getQuoteStatusLabels();

      const options = labels.map((label) => ({
        label,
        value: label,
      }));

      this.statusOptions.set(options);
    } catch (error) {
      console.error('Failed to load quote status labels', error);
      this.statusOptions.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
}
